import React, { useState, useEffect, useMemo } from 'react';
import {
  Table,
  Button,
  Space,
  message,
  Typography,
} from 'antd';
import {
  PlusOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { businessService, Business } from '../services/businesses';
import { useTableColumnSearch } from '../hooks/useTableColumnSearch';
import { useResizableColumns } from '../hooks/useResizableColumns';
import { useColumnVisibility } from '../hooks/useColumnVisibility';
import { ResizableTableHeader } from '../components/ResizableTableHeader';
import { ColumnVisibilityToggle } from '../components/ColumnVisibilityToggle';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission } from '../utils/permissions';
import '../styles/resizable-table.css';

const { Title } = Typography;

export const Businesses: React.FC = () => {
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<'all' | 'potential' | 'active' | 'inactive'>('all');
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const navigate = useNavigate();
  const { user } = useAuth();
  const canView = hasPermission(user, 'business.view');
  const canCreate = hasPermission(user, 'business.create');

  useEffect(() => {
    if (!canView) {
      setBusinesses([]);
      return;
    }
    loadBusinesses();
  }, [canView]);

  const loadBusinesses = async () => {
    if (!canView) {
      return;
    }
    setLoading(true);
    try {
      const response = await businessService.getAll();
      setBusinesses(response.data || []);
    } catch {
      message.error('Failed to load businesses');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await businessService.delete(id);
      message.success('Business deleted successfully');
      loadBusinesses();
    } catch {
      message.error('Failed to delete business');
    }
  };

  const handleImportNavigate = () => {
    navigate('/businesses/import');
  };

  const baseColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: 200,
      sorter: (a: Business, b: Business) => (a.name || '').localeCompare(b.name || ''),
      render: (val: string | undefined) => val || '-',
    },
    {
      title: 'Registration No.',
      dataIndex: 'registration_no',
      key: 'registration_no',
      width: 160,
      sorter: (a: Business, b: Business) => (a.registration_no || '').localeCompare(b.registration_no || ''),
    },
    {
      title: 'Entity',
      dataIndex: 'entity',
      key: 'entity',
      width: 140,
      sorter: (a: Business, b: Business) => (a.entity || '').localeCompare(b.entity || ''),
    },
    {
      title: 'SST No.',
      dataIndex: 'sst_no',
      key: 'sst_no',
      width: 140,
      sorter: (a: Business, b: Business) => (a.sst_no || '').localeCompare(b.sst_no || ''),
    },
    {
      title: 'Tin No.',
      dataIndex: 'tin_no',
      key: 'tin_no',
      width: 140,
      sorter: (a: Business, b: Business) => (a.tin_no || '').localeCompare(b.tin_no || ''),
    },
    {
      title: 'Incorporation',
      dataIndex: 'incorporation_date',
      key: 'incorporation_date',
      width: 140,
      sorter: (a: Business, b: Business) => (a.incorporation_date || '').localeCompare(b.incorporation_date || ''),
      render: (val: string | undefined) => val || '-',
    },
    {
      title: 'Year End',
      dataIndex: 'year_end',
      key: 'year_end',
      width: 120,
      sorter: (a: Business, b: Business) => (a.year_end || '').localeCompare(b.year_end || ''),
      render: (val: string | undefined) => val || '-',
    },
    {
      title: 'Financial Statement',
      dataIndex: 'financials',
      key: 'financial_statement',
      width: 180,
      sorter: (a: Business, b: Business) =>
        (a.financials?.[0]?.type || '').localeCompare(b.financials?.[0]?.type || ''),
      render: (_: unknown, record: Business) => record.financials?.[0]?.type || '-',
    },
    {
      title: 'Annual Return',
      dataIndex: 'annual_return',
      key: 'annual_return',
      width: 140,
      sorter: (a: Business, b: Business) => (a.annual_return || '').localeCompare(b.annual_return || ''),
      render: (val: string | undefined) => val || '-',
    },
    {
      title: 'Contact No.',
      dataIndex: 'contact_number',
      key: 'contact_number',
      width: 160,
      sorter: (a: Business, b: Business) =>
        (a.contacts?.[0]?.contact_number || '').localeCompare(b.contacts?.[0]?.contact_number || ''),
      render: (_: unknown, record: Business) => record.contacts?.[0]?.contact_number || '-',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      width: 200,
      sorter: (a: Business, b: Business) => (a.contacts?.[0]?.email || '').localeCompare(b.contacts?.[0]?.email || ''),
      render: (_: unknown, record: Business) => record.contacts?.[0]?.email || '-',
    },
  ];

  const {
    columnConfig,
    visibleColumns,
    visibleKeys,
    handleColumnVisibilityChange,
  } = useColumnVisibility(baseColumns);

  const { columns: resizableColumns } = useResizableColumns(visibleColumns);

  const memoizedColumns = useMemo(
    () =>
      resizableColumns.map((col) => ({
        ...col,
        width: col.width, // Preserve width
        onHeaderCell: col.onHeaderCell, // Preserve onHeaderCell for resizing
        getSearchValue:
          col.dataIndex === 'name'
            ? (record: Business) => record.name
            : col.dataIndex === 'registration_no'
            ? (record: Business) => record.registration_no
            : col.dataIndex === 'sst_no'
            ? (record: Business) => record.sst_no
            : col.dataIndex === 'tin_no'
            ? (record: Business) => record.tin_no
            : col.dataIndex === 'entity'
            ? (record: Business) => record.entity
            : col.dataIndex === 'engagement'
            ? (record: Business) => record.engagement
            : col.dataIndex === 'agent'
            ? (record: Business) => record.agent?.name
            : col.dataIndex === 'staff'
            ? (record: Business) => record.staff?.name
            : col.dataIndex === 'constitution'
            ? (record: Business) => (record.constitution ? 'Yes' : 'No')
            : col.dataIndex === 'common_seal'
            ? (record: Business) => (record.common_seal ? 'Yes' : 'No')
            : (undefined as unknown as (record: Business) => string | number | null | undefined),
      })),
    [resizableColumns]
  );

  const typeFilteredBusinesses = useMemo(() => {
    if (filter === 'potential') return businesses.filter(b => b.type === 'potential');
    if (filter === 'active') return businesses.filter(b => b.type === 'active');
    if (filter === 'inactive') return businesses.filter(b => b.type === 'inactive');
    return businesses;
  }, [businesses, filter]);

  const { columnsWithSearch, filteredData } = useTableColumnSearch<Business>(typeFilteredBusinesses, memoizedColumns as any);

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={3} style={{ margin: 0 }}>Businesses</Title>
        <Space>
          <ColumnVisibilityToggle
            columns={columnConfig}
            visibleKeys={visibleKeys}
            onChange={handleColumnVisibilityChange}
          />
          {canCreate && (
            <Button icon={<UploadOutlined />} onClick={handleImportNavigate}>
              Import
            </Button>
          )}
          {canCreate && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate('/businesses/new')}
            >
              Add Business
            </Button>
          )}
        </Space>
      </div>

      <div style={{ marginBottom: 16 }}>
        <Space>
          <Button 
            type={filter === 'all' ? 'primary' : 'default'}
            onClick={() => setFilter('all')}
          >
            All
          </Button>
          <Button 
            type={filter === 'potential' ? 'primary' : 'default'}
            onClick={() => setFilter('potential')}
          >
            Potential
          </Button>
          <Button 
            type={filter === 'active' ? 'primary' : 'default'}
            onClick={() => setFilter('active')}
          >
            Active
          </Button>
          <Button 
            type={filter === 'inactive' ? 'primary' : 'default'}
            onClick={() => setFilter('inactive')}
          >
            Inactive
        </Button>
        </Space>
      </div>

      <Table
        columns={columnsWithSearch}
        dataSource={filteredData}
        rowKey="id"
        loading={loading}
        pagination={false}
        scroll={{ y: 'calc(100vh - 400px)' }}
        components={{
          header: {
            cell: ResizableTableHeader,
          },
        }}
        rowSelection={{
          selectedRowKeys,
          onChange: (keys) => setSelectedRowKeys(keys),
          hideSelectAll: true,
          columnWidth: 0,
          renderCell: () => null,
        }}
        footer={() => (
          <div style={{ textAlign: 'left', padding: '8px 0' }}>
            Total: {filteredData.length} {filteredData.length === 1 ? 'business' : 'businesses'}
          </div>
        )}
        onRow={(record) => ({
          onClick: () => setSelectedRowKeys([record.id]),
          onDoubleClick: canView ? () => navigate(`/businesses/${record.id}/edit`) : undefined,
          style: { cursor: canView ? 'pointer' : 'default' },
        })}
      />
    </div>
  );
};
